import type { FC } from 'react'
import { useEffect } from 'react'

import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import FormControl from '@mui/material/FormControl'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import type { DisciplineVerzameling } from '@models/DisciplineVerzameling'

import { BooleanToggleGroup } from './BooleanToggleGroup'

interface Props {
  disciplineVerzamelingen: DisciplineVerzameling[]
  onChange: (disciplineVerzamelingen: DisciplineVerzameling[]) => void
  dvErrors: Record<string, string>
}

export const EditHoofdleidingPerDiscipline: FC<Props> = ({
  disciplineVerzamelingen,
  onChange,
  dvErrors,
}) => {
  const update = (
    dv: DisciplineVerzameling,
    updates: Partial<DisciplineVerzameling>,
  ) => {
    const updatedData = [...disciplineVerzamelingen]
    const index = disciplineVerzamelingen.findIndex(
      (d) =>
        d.disciplineCode === dv.disciplineCode &&
        d.netbeheerderCode === dv.netbeheerderCode,
    )

    // dv comes from this render's disciplineVerzamelingen.map.
    updatedData[index] = {
      ...updatedData[index],
      ...updates,
    }

    onChange(updatedData)
  }

  useEffect(() => {
    if (
      disciplineVerzamelingen.some(
        (dv) => typeof dv.isHoofdleidingAanleggen === 'undefined',
      )
    ) {
      const definedDiscVer = [...disciplineVerzamelingen].map((dv) => ({
        ...dv,
        isHoofdleidingAanleggen:
          typeof dv.isHoofdleidingAanleggen === 'undefined'
            ? false // default - voor unanswered handling van deze vraag, zie de Quickscan component
            : dv.isHoofdleidingAanleggen,
      }))

      onChange(definedDiscVer)
    }
  }, [disciplineVerzamelingen, onChange])

  return (
    <>
      {disciplineVerzamelingen.map((dv) => (
        <Accordion
          key={`${dv.disciplineCode}-${dv.netbeheerderCode}`}
          expanded={dv.isHoofdleidingAanleggen === true}
          slotProps={{ transition: { unmountOnExit: true } }}
        >
          <AccordionSummary component={'div'}>
            <Typography style={{ flex: 1 }}>
              Hoofdleiding aanleggen voor discipline {dv.disciplineLabel}
            </Typography>

            <BooleanToggleGroup
              value={dv.isHoofdleidingAanleggen}
              onChange={(bool) => update(dv, { isHoofdleidingAanleggen: bool })}
            />
          </AccordionSummary>

          <AccordionDetails>
            <Typography>
              Voor deze aanvraag moet hoofdleiding aangelegd worden. Geef aub de
              reden aan voor deze hoofdleiding
            </Typography>

            <FormControl style={{ width: '100%', marginTop: 10 }}>
              <TextField
                value={dv.isHoofdleidingAanleggenText}
                multiline
                rows={6}
                label={`Tekst (${
                  dv.isHoofdleidingAanleggenText?.length || 0
                }/500)`}
                onChange={(e) =>
                  update(dv, { isHoofdleidingAanleggenText: e.target.value })
                }
                required
                error={
                  !!dvErrors[
                    `${dv.disciplineCode}-${dv.netbeheerderCode}-hoofdleiding_aanleggen`
                  ]
                }
                helperText={
                  dvErrors[
                    `${dv.disciplineCode}-${dv.netbeheerderCode}-hoofdleiding_aanleggen`
                  ] || ''
                }
                slotProps={{ htmlInput: { maxLength: 500 } }}
              />
            </FormControl>
          </AccordionDetails>
        </Accordion>
      ))}
    </>
  )
}
